// Import react elements
import { useContext, useEffect, useRef, useState } from "react";

// Import strich api calls
import {
    fetchConnectionsData,
    deleteConnection,
} from "api/APIStrichCalls";

// Import context function
import { AuthContext } from "global/context/AuthContext";

// Import hooks
import usePageTitle from "global/hooks/usePageTitle";
import { useToast } from "global/hooks/useToast";

// Import components
import { Grid } from "global/components/Grid";
import { GridItem } from "global/components/Grid";
import CreateButton from "global/components/CreateButton";
import ConnectionsTable from "./components/ConnectionsTable";
import ConnectionDetailsPopUp from "./components/ConnectionDetailsPopUp";
import { DisplayConfirmationDialog } from "global/utilities/DialogBox";
import OpenStoreButton from "global/components/OpenStoreButton";
import { GetConnectionTypeDisplayName } from "global/utilities/ConnectionTypeDisplayNames";
import { GetStoreUrl } from "global/utilities/StoreURLs";
import HelpButton from "global/components/HelpButton";

// Import css stylesheet
import "./ConnectionsPage.css";



// ConnectionsPage.js
const ConnectionsPage = () => {


    //console.log(addingNewConnection);

    // Set title inside the page
    usePageTitle("Stores");


    
    // Get token from authContext
    const { token } = useContext(AuthContext);

    // Define toast options
    const { toastSuccess, toastError } = useToast();

    // Extract the connections data from the user context - allows us to use their data anywhere inside this component without needing to manually pass it down as a prop
    const [connectionsData, setConnectionsData] = useState(null);

    // Set empty array state for selected connection
    const [selectedConnection, setSelectedConnection] = useState([]);

    // Set a state for adding a new connection
    const [addingNewConnection, setAddingNewConnection] = useState(false);

    // Set false state for the connection details pop up
    const [connectionDetailsPopupOpen, setConnectionDetailsPopupOpen] = useState(false);

    // Remember the document position before opening a store
    const connectionsScrollPositionRef = useRef(0);

    // Keep restoring that position while the editor can refresh the table underneath
    const restoreConnectionsScrollPositionRef = useRef(false);

    // Loading state
    const [loadingConnections, setLoadingConnections] = useState(false);



    // Remember where the user currently is in the stores list
    const rememberConnectionsScrollPosition = () => {

        // Start with the browser window position
        let currentScrollPosition = window.scrollY;

        // Prefer the document scrolling element when available
        if (document.scrollingElement) {
            currentScrollPosition = document.scrollingElement.scrollTop;
        }

        // Store the current vertical position
        connectionsScrollPositionRef.current = currentScrollPosition;

        // Keep restoring it while the store popup can refresh the underlying table
        restoreConnectionsScrollPositionRef.current = true;

    };


    // Restore the stores list after an editor-triggered refresh
    useEffect(() => {

        // Do nothing unless a previous position is waiting to be restored
        if (!restoreConnectionsScrollPositionRef.current) {
            return;
        }

        // Wait until the stores have finished loading
        if (loadingConnections) {
            return;
        }

        // Wait until store data is available
        if (!Array.isArray(connectionsData)) {
            return;
        }

        // Store both animation frame ids for cleanup
        let firstAnimationFrameId = 0;
        let secondAnimationFrameId = 0;

        // Wait one frame for the table to render
        firstAnimationFrameId = window.requestAnimationFrame(() => {

            // Wait another frame for its layout to settle
            secondAnimationFrameId = window.requestAnimationFrame(() => {

                // Normalise the stored position
                let restoredScrollPosition = Number(connectionsScrollPositionRef.current);

                // Fall back to the top for an invalid stored position
                if (!Number.isFinite(restoredScrollPosition) || restoredScrollPosition < 0) {
                    restoredScrollPosition = 0;
                }

                // Restore the document position
                if (document.scrollingElement) {

                    document.scrollingElement.scrollTop = restoredScrollPosition;

                } else {

                    window.scrollTo(0, restoredScrollPosition);

                }

                // Keep it armed while the popup remains open because Save can refresh again
                if (!connectionDetailsPopupOpen) {
                    restoreConnectionsScrollPositionRef.current = false;
                }

            });

        });

        // Cancel pending restoration if another render happens first
        return () => {

            window.cancelAnimationFrame(firstAnimationFrameId);
            window.cancelAnimationFrame(secondAnimationFrameId);

        };

    }, [
        connectionsData,
        connectionDetailsPopupOpen,
        loadingConnections,
    ]);


    // Function to handle selecting a connection from the table to view more details in popup
    const handleSelectConnection = (connectionID) => {

        // Remember exactly where the user was before opening the store
        rememberConnectionsScrollPosition();

        // Handle array of ids if this is from the table row menu ...
        const connID = Array.isArray(connectionID) ? connectionID[0] : connectionID;

        // Update the selected connection
        setSelectedConnection(connID);

        fetchConnections(connID);

        // Open the pop up
        setConnectionDetailsPopupOpen(true);

    };



    // Fetch connections data so we can update the render
    const fetchConnections = async (connectionID) => {

        // Set loading state
        setLoadingConnections(true);

        // Refetch the connections to update table
        const response = await fetchConnectionsData(token);

        // Check if it was successful
        if (response.success) {

            // Hide system portal stores from the Stores page list
            const visibleConnections = response.results.filter((con) => con.conntype !== "STRICH_PORTAL");

            // Update the results with display fields for the connections table
            const connectionsWithStoreLinkDiv = visibleConnections.map((con) => {

                // Store the environment-specific URL for filtering and sorting
                const storeUrlText = con.alias ? GetStoreUrl(con.alias).replace(/^https?:\/\//, "") : "";

                // Show the primary Store used for new design defaults
                let primaryStoreIndicator = "";
                if (con.primary_store === "Y") {
                    primaryStoreIndicator = (
                        <span className="connections-table_primary-marker" title="Primary Store for new designs" aria-label="Primary Store">
                            <svg viewBox="0 0 24 24" width="17" height="17" aria-hidden="true">
                                <path d="M3 11 12 3l9 8v10h-6v-7H9v7H3z" fill="currentColor" />
                            </svg>
                        </span>
                    );
                }

                // Check if there is an alias
                if(con.alias) {

                    // Return the connection with the added display fields
                    return {
                        ...con,
                        conntype: GetConnectionTypeDisplayName(con.conntype),
                        primaryStoreIndicator: primaryStoreIndicator,
                        storeUrlText: storeUrlText,
                        storeUrl:
                            <span className="connections-table_store-url">
                                <span className="connections-table_store-url-text">{storeUrlText}</span>
                                <OpenStoreButton alias={con.alias} buttonTitle={"View Store"} />
                            </span>,             
                    };

                }

                // Return the connection with the display type
                return {
                    ...con,
                    conntype: GetConnectionTypeDisplayName(con.conntype),
                    primaryStoreIndicator: primaryStoreIndicator,
                    storeUrlText: storeUrlText,
                    storeUrl: ""
                };
                    
            });


            //console.log("connectionsWithStoreLinkDiv: ", connectionsWithStoreLinkDiv);


            // Update the connectionsData to update the table
            setConnectionsData(connectionsWithStoreLinkDiv);

            // If there is a connectionID provided 
            if (connectionID) {

                // Find the matching connection
                const matchedConnection = response.results.find(conn => conn.id === connectionID);

                // Set it as the selected connection
                setSelectedConnection(matchedConnection);

                // Open the pop up with the new connection
                setConnectionDetailsPopupOpen(true);

            }

        } else {

            // Toast error message
            toastError({ title: "Error", message: "Could not fetch stores: " + response.error_message, autoCloseMs: 6000 });

        }

        // Clear the loading state
        setLoadingConnections(false);

    }



    // Fetch connections once on mount
    useEffect(() => {

        fetchConnections();

    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);



    // Function to handle delete connection
    const handleDeleteConnection = async (connectionID) => {

        // Display confirmation
        DisplayConfirmationDialog(

            `<h3>Are you sure?</h3><br/><span class='warning-message'>WARNING: This cannot be undone.</span>`,
            "Delete",
            "Cancel",
            async function (is_confirmed) {

                // If user confirms, proceed with deletion
                if (is_confirmed) {

                    // Remember the current list position before refreshing it after deletion
                    rememberConnectionsScrollPosition();

                    // Delete connection by id
                    const response = await deleteConnection(token, connectionID);

                    // Check if it deleted
                    if (response.success) {

                        // Close the pop up
                        setConnectionDetailsPopupOpen(false);

                        // It did so re fetch the connections to update the table
                        fetchConnections();

                        // Toast success message
                        toastSuccess({ title: "Success", message: "Store deleted." });

                    } else {

                        // Toast error message
                        toastError({ title: "Error", message: "Could not delete store: " + response.error_message, autoCloseMs: 6000 });

                    }

                }

            }

        );

    };



    return (

        <>

            <Grid>

                 <GridItem>

                    <div className="horizontal-align-content-center flex-justify-left-right">

                        <h3>Stores <HelpButton section="stores" code="overview" label="Stores" /></h3>

                        {/* Add empty pop up when clicked */}
                        <CreateButton
                            title="+ Add New Store"
                            size="l"
                            onClick={() => {
                                setAddingNewConnection(true)
                                setConnectionDetailsPopupOpen(true)
                                setSelectedConnection(null)
                            }}
                        />

                    </div>
            
                    <ConnectionsTable 
                        connectionsData={connectionsData} 
                        setConnectionsData={setConnectionsData}
                        loadingConnections={loadingConnections}
                        handleSelectConnection={handleSelectConnection} 
                        token={token}
                        handleDeleteConnection={handleDeleteConnection}
                    />

                    <br/>

                    {connectionDetailsPopupOpen && 

                        <ConnectionDetailsPopUp
                            isOpen={connectionDetailsPopupOpen}
                            onClose={() => {

                                // Close the popup
                                setConnectionDetailsPopupOpen(false);

                                // Clear the selected connection
                                setSelectedConnection(null);

                                // Clear the 'adding new' flag
                                setAddingNewConnection(false);

                            }}
                            token={token}
                            connectionDetails={selectedConnection}
                            setConnectionDetails={setSelectedConnection}
                            addingNewConnection={addingNewConnection}
                            setAddingNewConnection={setAddingNewConnection}
                            fetchConnections={fetchConnections}
                            handleDeleteConnection={handleDeleteConnection}
                            loadingConnection={loadingConnections}
                        />

                    }

                </GridItem>

            </Grid>

        </>

    );

};

export default ConnectionsPage;